// Cart links handler.

const addProductToCart = async (items) => {
	const formData = {
		items: items,
	};
	const response = await fetch('/cart/add.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(formData),
	});

	if (!response.ok) {
		throw new Error('Failed to add product to cart');
	}
	return await response.json();
};

// Both elements exist only on the cart page (sections/main-cart-items-custom.liquid).
// Without the guards this threw a TypeError on every other page — and it did so AFTER the
// sessionStorage key had already been removed, so the parked items were lost for good. The
// throw also aborted the rest of the DOMContentLoaded handler, which meant `consent:params`
// was never dispatched and the consent sync failed on that page as well.
const setLoading = () => {
	const cartEmptyText = document.querySelector('.cart__empty-text');
	const cartLoadingText = document.querySelector('.cart__loading-text');
	if (cartEmptyText) cartEmptyText.style.display = 'none';
	if (cartLoadingText) cartLoadingText.style.display = 'block';
};

// Counterpart to setLoading(): without it the blinking "Dein Produkt wird hinzugefügt"
// stays on screen forever after a failed add (custom-style.css animates it), so the
// customer sees a loading state instead of the failure.
const unsetLoading = () => {
	const cartEmptyText = document.querySelector('.cart__empty-text');
	const cartLoadingText = document.querySelector('.cart__loading-text');
	if (cartLoadingText) cartLoadingText.style.display = 'none';
	if (cartEmptyText) cartEmptyText.style.display = '';
};

const checkCartItemsAndFooter = () => {
	// If we have an update but the rerendering failed. We reload the cart
	const cartItems = document.getElementById('main-cart-items');
	const cartFooter = document.getElementById('main-cart-footer');

	// Same failure class as setLoading(): this runs 3.5s after every add, including on
	// pages that have no cart markup at all.
	if (!cartItems || !cartFooter) return;

	// fetch the cart again
	if (cartItems.classList.contains('is-empty') || cartFooter.classList.contains('is-empty')) {
		// check if the cart is empty
		console.log('Cart is empty fetche cart');
		fetch(`${Shopify.routes.root}cart.js`)
			.then((response) => response.json())
			.then((cart) => {
				if (cart.item_count > 0) {
					window.location.reload();
				}
			})
			.catch((e) => {
				console.error(e);
			});
	}
};

const hasBorlabsCookie = () => {
	return document.cookie.split(';').some((item) => item.trim().startsWith('borlabs-cookie='));
};

const addPendingCartItems = (items) => {
	setLoading();
	setTimeout(() => {
		addProductToCart(items)
			.then((response) => {
				console.log('Product added to cart', response);
				// Fetch full cart to get discounted prices, then publish custom tracking event
				fetch(`${Shopify.routes.root}cart.js`)
					.then((res) => res.json())
					.then((cart) => {
						if (Shopify?.analytics) {
							const changedVariantId = String(items[0]?.id);
							const changedItem = cart.items.find((i) => String(i.variant_id) === changedVariantId);
							const addedQty = parseInt(items[0]?.quantity || 1, 10);
							const previousQuantity = (changedItem?.quantity ?? addedQty) - addedQty;
							const unitPriceCents = changedItem?.price ?? 0;
							Shopify.analytics.publish('custom:cart_updated', {
								cart,
								changedVariantId,
								itemTitle: null, // resolved via changedVariantId from cart.items
								previousQuantity,
								unitPriceCents,
							});
						}
					})
					.catch((err) => console.error('Failed to fetch cart for tracking', err));
				setTimeout(() => {
					document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
				}, 10);
				setTimeout(() => {
					// fallback if the cart is not rerendered
					checkCartItemsAndFooter();
				}, 3500);
			})
			.catch((error) => {
				console.error('Failed to add product to cart', error);
				unsetLoading();
			});
	}, 500);
};

// --- Einlösen der geparkten Warenkorb-Artikel ------------------------------------------
// Last line of defence: even if consent-handler.js never answers — because the script
// itself is blocked, or the consent API never appears — the parked items have to reach the
// cart. A consent error must never affect the cart function.
const PENDING_ITEMS_WATCHDOG = 15000;

let pendingItemsFlushed = false;

// The ONLY place that redeems parked items. Callable from every path, effective exactly
// once. That single-shot guard is what prevents the double booking: previously three
// independent call sites could each consume the same items across a page load and an
// aborted deeplink session, and /cart/add.js adds quantities up.
const flushPendingCartItems = (reason, { stopConsentReload = false } = {}) => {
	if (pendingItemsFlushed) return;
	const pendingItemsJSON = sessionStorage.getItem('pendingCartItems');
	if (!pendingItemsJSON) return;
	pendingItemsFlushed = true;
	sessionStorage.removeItem('pendingCartItems');
	if (stopConsentReload) {
		// We stopped waiting for the consent. A reload arriving later would abort the
		// /cart/add.js request that is now in flight.
		sessionStorage.setItem('consentReloadedOnce', '1');
	}
	console.log(`Adding pending cart items (${reason})`);
	try {
		addPendingCartItems(JSON.parse(pendingItemsJSON));
	} catch (error) {
		console.error('Pending cart items could not be parsed', error);
	}
};

// Fired by consent-handler when Shopify consent already matches Borlabs (no reload will happen)
document.addEventListener('consent:already-synced', () => flushPendingCartItems('consent already synced'));

// A failed consent sync used to be invisible to the visitor: the Shopify consent silently kept
// whatever it was, while `hide-banner` is only removed when NO Borlabs cookie was found — so
// somebody who had decided on WordPress was left with a different state in the shop and no way
// to notice or change it. Showing Shopify's own banner gives them that way back.
// Only on `terminal` failures: the load-features error is reported too, but the write is still
// attempted after it and may succeed.
// Registered at top level so the listener exists before the consent chain can answer.
document.addEventListener('consent:failed', (event) => {
	if (!event.detail?.terminal) return;
	console.warn(`Consent sync failed (${event.detail.stage}), showing consent banner`);
	document.querySelector('body')?.classList.remove('hide-banner');
});

document.addEventListener('DOMContentLoaded', () => {
	const URLIdentifier = 'cart';
	const params = new URLSearchParams(window.location.search);
	const isDeeplink = window.location.href.includes(URLIdentifier) && params.has('ids');

	// (1) On a deeplink load the URL is what counts.
	if (isDeeplink) {
		// Items left behind by an aborted session would otherwise be booked on top of the
		// deeplink item — /cart/add.js adds quantities, so the customer ended up with 2.
		const stranded = sessionStorage.getItem('pendingCartItems');
		if (stranded) {
			sessionStorage.removeItem('pendingCartItems');
			console.warn('Discarding stranded pendingCartItems from an aborted session:', stranded);
		}
		const items = params.get('ids').split(',').map((variantElement) => {
			const [id, quantity] = variantElement.split(':');
			return { id, quantity };
		});
		// Strip URL immediately so any reload never re-triggers the add
		window.history.replaceState({}, document.title, window.location.pathname);
		sessionStorage.setItem('pendingCartItems', JSON.stringify(items));
	}

	// (2) Arm the redemption path BEFORE consent:params is dispatched, so no signal can be
	// missed. `consent:settled` is the fast path — the consent handler sends it once the
	// consent is final for this page load, including on its failure paths. The watchdog is
	// the guarantee and covers the case where no signal arrives at all.
	const consentSyncPending = hasBorlabsCookie() && sessionStorage.getItem('consentReloadedOnce') !== '1';
	if (consentSyncPending) {
		document.addEventListener('consent:settled', () => flushPendingCartItems('consent settled'), { once: true });
		setTimeout(() => flushPendingCartItems('consent watchdog', { stopConsentReload: true }), PENDING_ITEMS_WATCHDOG);
	}

	if (hasBorlabsCookie()) {
		console.log('Borlabs cookie found, dispatching consent:params event');
		document.dispatchEvent(new CustomEvent('consent:params', { bubbles: true, detail: { params } }));
	} else {
		// No borlabs cookie found, show the consent banner
		console.log('No borlabs cookie found, showing consent banner');
		document.querySelector('body').classList.remove('hide-banner');
	}

	// (3) No consent sync to wait for — this is the load after a consent reload, or a visitor
	// without a Borlabs cookie. Redeem straight away.
	if (!consentSyncPending) {
		flushPendingCartItems('no consent sync pending');
	}
});
